import { useCallback, useEffect, useState } from "react";
import Button from "../components/atoms/Button";
import IconButton from "../components/atoms/IconButton";
import Input from "../components/atoms/Input";
import Spinner from "../components/atoms/Spinner";
import { CollapsiblePanel } from "../components/molecules";
import { CategoryModal, GuideModal } from "../components/organisms";
import { useProfile } from "../hooks/auth";
import { useAvailableLocales } from "../hooks/data-review";
import { useRealms } from "../hooks/realms";
import { GuidesAdminService } from "../services/guides-admin";
import { LocaleService } from "../services/locale";
import { RealmsService } from "../services/realms";
import type {
  Category,
  CategoryFormData,
  CopyGuidesToLocaleResponse,
  Guide,
  GuideFilters,
  GuideFormData,
} from "../types/guides";
import { downloadGuideAsMarkdown } from "../utils/guide-export";
import { readMarkdownFile } from "../utils/markdown-parser";

// Convert Category to GuideCategory for modal compatibility
function categoryToGuideCategory(category: Category) {
  return {
    id: category.id.toString(),
    name: category.name,
    displayName: category.displayName,
    description: category.description ?? "",
    icon: "📁", // Default icon
    createdAt: new Date(category.createdAt),
    updatedAt: new Date(category.updatedAt),
  };
}

export function GuidesPage() {
  const { data: user } = useProfile();
  const { data: realms } = useRealms();
  const [selectedRealmId, setSelectedRealmId] = useState<number | null>(null);
  const { data: availableLocales } = useAvailableLocales(
    selectedRealmId ?? undefined
  );
  const locale = LocaleService.getSelectedLocale();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<GuideFilters>({
    search: "",
    tags: [],
    category: null,
  });
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    guideName: string;
    guideTitle: string;
  }>({ show: false, guideName: "", guideTitle: "" });
  const [copyState, setCopyState] = useState<{
    targetLocale: string;
    overwrite: boolean;
    copying: boolean;
    result: CopyGuidesToLocaleResponse | null;
  }>({
    targetLocale: "",
    overwrite: false,
    copying: false,
    result: null,
  });
  const [bulkImportState, setBulkImportState] = useState<{
    importing: boolean;
    result: {
      updated: string[];
      skipped: string[];
      failed: Array<{ name: string; error: string }>;
    } | null;
  }>({
    importing: false,
    result: null,
  });

  // Initialize selected realm from user, localStorage, or first available realm
  useEffect(() => {
    if (selectedRealmId === null && (user || realms)) {
      const storedRealmId = RealmsService.getSelectedRealm();

      if (storedRealmId) {
        setSelectedRealmId(storedRealmId);
      } else if (user?.realmId) {
        setSelectedRealmId(user.realmId);
      } else if (realms && realms.length > 0) {
        const firstRealm = realms.at(0);
        if (firstRealm) {
          setSelectedRealmId(firstRealm.id);
        }
      }
    }
  }, [user, realms, selectedRealmId]);

  // Save selected realm to localStorage when it changes
  useEffect(() => {
    if (selectedRealmId !== null) {
      RealmsService.setSelectedRealm(selectedRealmId);
    }
  }, [selectedRealmId]);

  const loadData = useCallback(async () => {
    if (selectedRealmId === null || !locale) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [guidesData, categoriesData] = await Promise.all([
        GuidesAdminService.getGuides({
          realmId: selectedRealmId,
          locale,
        }),
        GuidesAdminService.getCategories(),
      ]);

      setGuides(guidesData);
      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedRealmId, locale]);

  useEffect(() => {
    if (selectedRealmId !== null && locale) {
      void loadData();
    }
  }, [selectedRealmId, locale, loadData]);

  const handleCopyToLocale = async () => {
    if (!copyState.targetLocale || copyState.targetLocale === locale) {
      setError("Please select a different target locale");
      return;
    }

    try {
      setCopyState((prev) => ({ ...prev, copying: true }));
      setError(null);

      const result = await GuidesAdminService.copyGuidesToLocale({
        sourceLocale: locale,
        targetLocale: copyState.targetLocale,
        overwrite: copyState.overwrite,
      });

      setCopyState((prev) => ({ ...prev, copying: false, result }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to copy guides");
      setCopyState((prev) => ({ ...prev, copying: false }));
    }
  };

  const handleCloseCopyModal = () => {
    setCopyState({
      targetLocale: "",
      overwrite: false,
      copying: false,
      result: null,
    });
  };

  const handleSaveGuide = async (data: GuideFormData) => {
    try {
      setError(null);

      if (editingGuide) {
        // Update existing guide (locale is sent via header automatically)
        const updatedGuide = await GuidesAdminService.updateGuide(
          editingGuide.metadata.name,
          {
            title: data.title,
            description: data.description,
            category: data.category,
            tags: data.tags,
            content: data.content,
          }
        );
        setGuides((prev) =>
          prev.map((g) =>
            g.metadata.name === editingGuide.metadata.name ? updatedGuide : g
          )
        );
      } else {
        // Create new guide (locale is sent via header automatically)
        const newGuide = await GuidesAdminService.createGuide({
          name: data.name,
          title: data.title,
          description: data.description,
          category: data.category,
          tags: data.tags,
          content: data.content,
        });
        setGuides((prev) => [...prev, newGuide]);
      }

      setEditingGuide(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save guide");
    }
  };

  const handleDeleteGuide = (guideName: string, guideTitle: string) => {
    setDeleteConfirm({ show: true, guideName, guideTitle });
  };

  const confirmDelete = async () => {
    try {
      setError(null);
      // Locale is sent via header automatically
      await GuidesAdminService.deleteGuide(deleteConfirm.guideName);
      setGuides((prev) =>
        prev.filter((g) => g.metadata.name !== deleteConfirm.guideName)
      );
      setDeleteConfirm({ show: false, guideName: "", guideTitle: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete guide");
    }
  };

  const handleSaveCategory = async (data: CategoryFormData) => {
    try {
      setError(null);

      const newCategory = await GuidesAdminService.createCategory({
        name: data.name,
        displayName: data.displayName,
        description: data.description,
      });
      setCategories((prev) => [...prev, newCategory]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create category"
      );
    }
  };

  const handleEditGuide = (guide: Guide) => {
    setEditingGuide(guide);
    setShowGuideModal(true);
  };

  const handleCloseGuideModal = () => {
    setShowGuideModal(false);
    setEditingGuide(null);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setBulkImportState({ importing: true, result: null });
      setError(null);

      // Parse all markdown files
      const parsedFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          try {
            const parsed = await readMarkdownFile(file);
            return parsed;
          } catch (err) {
            console.error(`Failed to parse ${file.name}:`, err);
            return null;
          }
        })
      );

      // Filter out failed parses and prepare update data
      const updates = parsedFiles
        .filter((parsed): parsed is NonNullable<typeof parsed> => parsed !== null)
        .map((parsed) => ({
          name: parsed.metadata.name,
          title: parsed.metadata.title,
          description: parsed.metadata.description,
          category: parsed.metadata.category,
          tags: parsed.metadata.tags,
          content: parsed.content,
        }));

      if (updates.length === 0) {
        setError('No valid markdown files found. Files must have frontmatter with a "name" field.');
        setBulkImportState({ importing: false, result: null });
        return;
      }

      // Bulk update guides
      const result = await GuidesAdminService.bulkUpdateGuides(updates);

      setBulkImportState({ importing: false, result });

      // Reload guides to show updated data
      await loadData();

      // Clear file input
      event.target.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import guides');
      setBulkImportState({ importing: false, result: null });
    }
  };

  const handleCloseBulkImportModal = () => {
    setBulkImportState({ importing: false, result: null });
  };

  // Filter guides based on search and category
  const filteredGuides = guides.filter((guide) => {
    const matchesSearch =
      GuidesAdminService.searchGuides([guide], filters.search).length > 0;
    const matchesCategory = filters.category
      ? guide.metadata.category === filters.category
      : true;
    return matchesSearch && matchesCategory;
  });

  // const allTags = GuidesAdminService.getAllTags(guides); // For future tag filtering feature

  if (loading) {
    return (
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-secondary rounded-lg border border-border-light p-8 text-center">
            <Spinner size="lg" variant="primary" />
            <p className="text-text-muted mt-4">Loading guides...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-text-primary">
              Guides Administration
            </h1>
            <p className="text-text-secondary mt-2">
              Manage knowledge base guides and documentation
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={() => setShowCategoryModal(true)}
              variant="secondary"
              size="medium"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              }
              iconPosition="left"
            >
              New Category
            </Button>
            <Button
              onClick={() => document.getElementById('bulk-import-input')?.click()}
              variant="secondary"
              size="medium"
              disabled={bulkImportState.importing}
              icon={
                bulkImportState.importing ? (
                  <Spinner size="sm" variant="primary" />
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                )
              }
              iconPosition="left"
            >
              {bulkImportState.importing ? 'Importing...' : 'Bulk Import'}
            </Button>
            <input
              id="bulk-import-input"
              type="file"
              accept=".md,.markdown"
              multiple
              onChange={handleBulkImport}
              style={{ display: 'none' }}
            />
            <Button
              onClick={() => {
                setEditingGuide(null);
                setShowGuideModal(true);
              }}
              variant="primary"
              size="medium"
              icon={
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              }
              iconPosition="left"
            >
              New Guide
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-danger-light border border-danger-border rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-danger-text">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-danger-text hover:text-danger-hover"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Bulk Import Results */}
        {bulkImportState.result && (
          <div className="mb-6 p-4 bg-bg-secondary border border-border-light rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-text-primary">Bulk Import Results</h3>
              <button
                onClick={handleCloseBulkImportModal}
                className="text-text-muted hover:text-text-primary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {bulkImportState.result.updated.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Updated ({bulkImportState.result.updated.length}):
                  </h4>
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="text-sm text-text-secondary space-y-1">
                      {bulkImportState.result.updated.map((name) => (
                        <li key={name} className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {bulkImportState.result.skipped.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Skipped - Not Found ({bulkImportState.result.skipped.length}):
                  </h4>
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="text-sm text-text-muted space-y-1">
                      {bulkImportState.result.skipped.map((name) => (
                        <li key={name} className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-yellow-500 rounded-full"></span>
                          {name} (guide does not exist)
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {bulkImportState.result.failed.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Failed ({bulkImportState.result.failed.length}):
                  </h4>
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="text-sm text-danger-text space-y-1">
                      {bulkImportState.result.failed.map((item) => (
                        <li key={item.name} className="flex items-start gap-2">
                          <span className="w-1 h-1 bg-red-500 rounded-full mt-1.5"></span>
                          <span>{item.name}: {item.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Copy Guides Panel */}
        {guides.length > 0 && (
          <CollapsiblePanel
            title="Copy Guides to Another Locale"
            className="mb-6"
          >
            {copyState.result ? (
              // Show results
              <div className="space-y-4">
                <div className="p-4 bg-action-key-lighter rounded-lg">
                  <p className="text-sm text-action-key-darker mb-2">
                    <strong>Copy completed!</strong>
                  </p>
                  <p className="text-sm text-text-secondary">
                    From {copyState.result.sourceLocale} to{" "}
                    {copyState.result.targetLocale}
                  </p>
                </div>

                {copyState.result.copied.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-2">
                      Copied ({copyState.result.copied.length}):
                    </h4>
                    <div className="max-h-32 overflow-y-auto">
                      <ul className="text-sm text-text-secondary space-y-1">
                        {copyState.result.copied.map((name) => (
                          <li key={name} className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-green-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            {name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {copyState.result.skipped.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-2">
                      Skipped ({copyState.result.skipped.length}):
                    </h4>
                    <div className="max-h-32 overflow-y-auto">
                      <ul className="text-sm text-text-muted space-y-1">
                        {copyState.result.skipped.map((name) => (
                          <li key={name} className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-yellow-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            {name} (already exists)
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {copyState.result.failed.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-2">
                      Failed ({copyState.result.failed.length}):
                    </h4>
                    <div className="max-h-32 overflow-y-auto">
                      <ul className="text-sm text-danger-text space-y-1">
                        {copyState.result.failed.map((item) => (
                          <li
                            key={item.name}
                            className="flex items-center gap-2"
                          >
                            <svg
                              className="w-4 h-4 text-red-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            {item.name}: {item.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button variant="primary" onClick={handleCloseCopyModal}>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              // Show form
              <div className="space-y-4">
                <p className="text-sm text-text-muted">
                  Copy all {guides.length} guides from <strong>{locale}</strong>{" "}
                  to another locale.
                </p>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Target Locale
                  </label>
                  <select
                    value={copyState.targetLocale}
                    onChange={(e) =>
                      setCopyState((prev) => ({
                        ...prev,
                        targetLocale: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-bg-primary text-text-primary focus:border-action-key-default focus:ring-1 focus:ring-action-key-default focus:outline-none"
                  >
                    <option value="">Select target locale...</option>
                    {(availableLocales ?? [])
                      .filter((l) => l !== locale)
                      .map((locale) => (
                        <option key={locale} value={locale}>
                          {locale}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="overwrite"
                    checked={copyState.overwrite}
                    onChange={(e) =>
                      setCopyState((prev) => ({
                        ...prev,
                        overwrite: e.target.checked,
                      }))
                    }
                    className="rounded border-border-medium text-action-key-default focus:ring-action-key-default"
                  />
                  <label
                    htmlFor="overwrite"
                    className="text-sm text-text-secondary"
                  >
                    Overwrite existing guides in target locale
                  </label>
                </div>

                <div className="flex space-x-4 pt-4">
                  <Button
                    variant="primary"
                    onClick={() => void handleCopyToLocale()}
                    disabled={!copyState.targetLocale || copyState.copying}
                  >
                    {copyState.copying ? (
                      <span className="flex items-center gap-2">
                        <Spinner size="sm" variant="white" />
                        Copying...
                      </span>
                    ) : (
                      `Copy ${guides.length} Guides`
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CollapsiblePanel>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-bg-secondary rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-action-key-lighter rounded-lg">
                <svg
                  className="w-5 h-5 text-action-key-darker"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-text-secondary">
                  Total Guides
                </p>
                <p className="text-xl font-bold text-text-primary">
                  {guides.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-bg-secondary rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-action-key-lighter rounded-lg">
                <svg
                  className="w-5 h-5 text-action-key-darker"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-text-secondary">
                  Categories
                </p>
                <p className="text-xl font-bold text-text-primary">
                  {categories.length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-bg-secondary rounded-lg p-4 border border-border-light">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-action-key-lighter rounded-lg">
                <svg
                  className="w-5 h-5 text-action-key-darker"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-text-secondary">
                  Filtered Results
                </p>
                <p className="text-xl font-bold text-text-primary">
                  {filteredGuides.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-bg-secondary rounded-lg border border-border-light p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Input
                    placeholder="Search guides by title, content, or tags..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    leftIcon={
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    }
                  />
                  {filters.search && (
                    <button
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, search: "" }))
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter */}
              <div className="relative">
                <select
                  value={filters.category ?? ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      category: e.target.value || null,
                    }))
                  }
                  className="px-3 py-2 border border-border-medium rounded-md bg-bg-primary text-text-primary focus:border-action-key-default focus:ring-1 focus:ring-action-key-default focus:outline-none appearance-none pr-8 min-w-[180px]"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.displayName}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-bg-surface rounded-md p-1">
              <IconButton
                onClick={() => setViewMode("grid")}
                variant={viewMode === "grid" ? "key" : "ghost"}
                size="small"
                title="Grid view"
                aria-label="Grid view"
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                }
              />
              <IconButton
                onClick={() => setViewMode("list")}
                variant={viewMode === "list" ? "key" : "ghost"}
                size="small"
                title="List view"
                aria-label="List view"
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                }
              />
            </div>
          </div>
        </div>

        {/* Guides Grid/List */}
        <div className="flex-1 overflow-auto">
          {filteredGuides.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-content-tertiary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">
                No guides found
              </h3>
              <p className="mt-1 text-sm text-text-secondary">
                {filters.search || filters.category
                  ? "Try adjusting your filters"
                  : "Get started by creating a new guide."}
              </p>
            </div>
          ) : (
            <div
              className={
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
                  : "space-y-2"
              }
            >
              {filteredGuides.map((guide) => (
                <div
                  key={guide.metadata.name}
                  className={`group bg-bg-secondary border border-border-light hover:border-border-medium transition-colors ${
                    viewMode === "grid" ? "rounded-lg p-4" : "rounded-md p-3"
                  }`}
                >
                  {viewMode === "grid" ? (
                    // Grid layout
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-text-primary truncate mb-1">
                            {guide.metadata.title}
                          </h3>
                          <p className="text-sm text-text-muted mb-2">
                            {guide.metadata.category ?? "Uncategorized"}
                          </p>
                          <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                            {guide.metadata.description ??
                              "No description available"}
                          </p>

                          {/* Tags */}
                          {guide.metadata.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {guide.metadata.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-block px-2 py-1 text-xs bg-action-key-lighter text-action-key-darker rounded-md"
                                >
                                  {tag}
                                </span>
                              ))}
                              {guide.metadata.tags.length > 3 && (
                                <span className="inline-block px-2 py-1 text-xs text-text-muted">
                                  +{guide.metadata.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <IconButton
                            onClick={() => downloadGuideAsMarkdown(guide)}
                            variant="ghost"
                            size="small"
                            title="Export as Markdown"
                            aria-label="Export guide as Markdown"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            }
                          />
                          <IconButton
                            onClick={() => handleEditGuide(guide)}
                            variant="ghost"
                            size="small"
                            title="Edit guide"
                            aria-label="Edit guide"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            }
                          />
                          <IconButton
                            onClick={() =>
                              handleDeleteGuide(
                                guide.metadata.name,
                                guide.metadata.title
                              )
                            }
                            variant="ghost"
                            size="small"
                            title="Delete guide"
                            aria-label="Delete guide"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            }
                          />
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-3 border-t border-border-light text-xs text-text-muted">
                        <span>
                          Updated{" "}
                          {guide.metadata.lastUpdated
                            ? new Date(
                                guide.metadata.lastUpdated
                              ).toLocaleDateString()
                            : "Unknown"}
                        </span>
                        <span>v{guide.metadata.version ?? "1.0"}</span>
                      </div>
                    </>
                  ) : (
                    // List layout - more dense
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-base font-medium text-text-primary truncate">
                            {guide.metadata.title}
                          </h3>
                          <span className="inline-block px-2 py-1 text-xs bg-action-key-lighter text-action-key-darker rounded">
                            {guide.metadata.category ?? "Uncategorized"}
                          </span>
                          {guide.metadata.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-1.5 py-0.5 text-xs text-text-muted bg-bg-surface rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {guide.metadata.tags.length > 2 && (
                            <span className="text-xs text-text-muted">
                              +{guide.metadata.tags.length - 2}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary truncate mt-1">
                          {guide.metadata.description ||
                            "No description available"}
                        </p>
                      </div>

                      <div className="flex items-center space-x-3 ml-4">
                        <span className="text-xs text-text-muted whitespace-nowrap">
                          {guide.metadata.lastUpdated
                            ? new Date(
                                guide.metadata.lastUpdated
                              ).toLocaleDateString()
                            : "Unknown"}
                        </span>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <IconButton
                            onClick={() => downloadGuideAsMarkdown(guide)}
                            variant="ghost"
                            size="small"
                            title="Export as Markdown"
                            aria-label="Export guide as Markdown"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            }
                          />
                          <IconButton
                            onClick={() => handleEditGuide(guide)}
                            variant="ghost"
                            size="small"
                            title="Edit guide"
                            aria-label="Edit guide"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            }
                          />
                          <IconButton
                            onClick={() =>
                              handleDeleteGuide(
                                guide.metadata.name,
                                guide.metadata.title
                              )
                            }
                            variant="ghost"
                            size="small"
                            title="Delete guide"
                            aria-label="Delete guide"
                            icon={
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Guide Modal */}
      <GuideModal
        isOpen={showGuideModal}
        onClose={handleCloseGuideModal}
        onSave={(data) => void handleSaveGuide(data)}
        onDelete={(id, title) => void handleDeleteGuide(id, title)}
        guide={editingGuide}
        categories={categories.map(categoryToGuideCategory)}
      />

      {/* Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={handleCloseCategoryModal}
        onSave={(data) => void handleSaveCategory(data)}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-primary rounded-lg p-6 max-w-md w-full mx-4 border border-border-light">
            <h3 className="text-lg font-medium text-text-primary mb-4">
              Delete Guide
            </h3>
            <p className="text-text-muted mb-6">
              Are you sure you want to delete &quot;{deleteConfirm.guideTitle}
              &quot;? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <Button
                variant="secondary"
                onClick={() =>
                  setDeleteConfirm({
                    show: false,
                    guideName: "",
                    guideTitle: "",
                  })
                }
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void confirmDelete()}
                fullWidth
                className="bg-danger-border hover:bg-danger-text"
              >
                Delete Guide
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
